import { dealsApi } from "@/api/deals-api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import { useUser } from "@clerk/clerk-react";
import { useCurrentDealStore } from "@/store/current-deal-store";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getErrorMessage } from "@/lib/api-error";
import { dealBoardCommentsApi } from "@/api/deal-board-comments-api";
import { raiseBoardApi } from "@/api/raise-board-api";
import {
  buildWallEntries,
  filterWallEntries,
  labelOfComment,
  sourceOptions,
  INTERNAL_SOURCE_LABEL,
  type WallSource,
} from "@/components/deal-detail/data-lake/wall-entries";
import { formatDistanceToNow } from "date-fns";
import { Globe, Pencil, Tag, Trash2 } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Link } from "react-router";
import posthog from "posthog-js";
import { toast } from "sonner";

/**
 * The composer's target. "internal" is a workspace-private deal note — the
 * default, and what this box did before board comments were merged in. Anything
 * else posts into a Raiseboard thread and is visible outside the workspace.
 */
type ComposeScope = "internal" | "board" | `lookbook:${string}`;

export default function DealWallComponent({ deal }: { deal: DealTransformed }) {
  const [noteText, setNoteText] = useState("");
  const { activeWorkspaceId } = useWorkspaceStore();
  const { isLoaded: isUserLoaded, isSignedIn, user } = useUser();
  const { updateDeal } = useCurrentDealStore();
  const queryClient = useQueryClient();
  const [displayCount, setDisplayCount] = useState(10);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const tagFilterId = useId();
  const sourceFilterId = useId();
  const availableTags = ["Idea", "Tech", "Team", "Marketing", "Traction"];
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [filterSources, setFilterSources] = useState<string[]>([]);
  const [composeScope, setComposeScope] = useState<ComposeScope>("internal");

  // Comments left on this deal out on the Raiseboard — the public board, the
  // global board, and any lookbook holding it. Fetched separately from the deal
  // itself: they live in their own collection, they change without the deal
  // changing, and the deal store is a zustand singleton with no room for a
  // second async source.
  const boardCommentsKey = [
    "deal-board-comments",
    activeWorkspaceId,
    deal.id,
  ];
  const { data: boardData } = useQuery({
    queryKey: boardCommentsKey,
    queryFn: () =>
      dealBoardCommentsApi.list(activeWorkspaceId as string, deal.id),
    enabled: !!activeWorkspaceId && !!deal.id,
  });
  // Memoized rather than defaulted inline: a fresh `[]` on every render while
  // the query is still loading would defeat the memos that depend on them.
  const boardComments = useMemo(
    () => boardData?.comments ?? [],
    [boardData?.comments]
  );
  const boardLookbooks = useMemo(
    () => boardData?.lookbooks ?? [],
    [boardData?.lookbooks]
  );

  const refreshBoardComments = () =>
    queryClient.invalidateQueries({ queryKey: boardCommentsKey });

  const { mutate: addDealNoteMutation, isPending } = useMutation({
    mutationFn: async () => {
      // Validate note content
      const trimmedText = noteText.trim();
      if (!trimmedText) {
        toast.error("Note cannot be empty");
        throw new Error("Note cannot be empty");
      }

      // Extract hashtags without the # symbol
      const hashTags =
        trimmedText.match(/#[\w]+/g)?.map((tag) => tag.slice(1)) || [];
      const allTags = [...new Set([...hashTags, ...selectedTags])];

      // Remove hashtags and their text from the note content
      const cleanNoteText = trimmedText.replace(/#[\w]+/g, "").trim();

      // Check if the note only contains tags
      if (!cleanNoteText && allTags.length > 0) {
        toast.error("Note must contain text content, not just tags");
        throw new Error("Note must contain text content, not just tags");
      }

      if (!activeWorkspaceId) {
        toast.error("No workspace selected. Please select a workspace.");
        throw new Error("No active workspace selected");
      }

      if (!isUserLoaded || !isSignedIn || !user || !user.id) {
        toast.error("Unable to identify user. Please sign in again.");
        throw new Error("No active user session");
      }

      const userId = user.id;
      const userDisplayName =
        user.fullName ||
        [user.firstName, user.lastName].filter(Boolean).join(" ") ||
        user.username ||
        user.primaryEmailAddress?.emailAddress ||
        "Unknown user";

      toast.loading("Adding note...");
      const response = await dealsApi.addDealNote(
        deal.id,
        cleanNoteText, // Use the clean note text without hashtags
        activeWorkspaceId,
        userId,
        allTags
      );

      // Update the deal with new note
      const updatedDealNotes = [
        ...deal.dealNotes,
        {
          _id: response.application.deal_notes[
            response.application.deal_notes.length - 1
          ]._id,
          note: cleanNoteText, // Store the clean note text
          created_by_user: userDisplayName,
          created_by_user_id: userId,
          created_on: new Date().toISOString(),
          tags: allTags,
        },
      ];

      updateDeal({
        ...deal,
        dealNotes: updatedDealNotes,
      });

      return response;
    },
    onSuccess: () => {
      toast.dismiss();
      toast.success("Note added successfully");
      posthog.capture("deal_note_added", {
        workspace_id: activeWorkspaceId,
        user_id: user?.id,
        clerk_id: user?.id,
        deal_id: deal.id,
        company_id: deal.id,
        note_length: noteText.trim().replace(/#[\w]+/g, "").trim().length,
      });
      setNoteText("");
      setSelectedTags([]);
    },
    onError: (error) => {
      toast.dismiss();
      const message =
        error instanceof Error
          ? error.message
          : "Failed to add note. Please try again.";
      toast.error(getErrorMessage(error, `Failed to add note: ${message}`));
    },
  });

  // Update the edit note state to include tags
  const [editNote, setEditNote] = useState<{
    isOpen: boolean;
    noteId: string;
    noteText: string;
    tags: string[];
  }>({
    isOpen: false,
    noteId: "",
    noteText: "",
    tags: [],
  });

  // Update the edit mutation to include tags
  const { mutate: editDealNoteMutation, isPending: isEditPending } =
    useMutation({
      mutationFn: async () => {
        // Validate note content
        const trimmedText = editNote.noteText.trim();
        if (!trimmedText) {
          throw new Error("Note cannot be empty");
        }

        // Extract hashtags without the # symbol
        const hashTags =
          trimmedText.match(/#[\w]+/g)?.map((tag) => tag.slice(1)) || [];
        const allTags = [...new Set([...hashTags, ...editNote.tags])];

        // Remove hashtags and their text from the note content
        const cleanNoteText = trimmedText.replace(/#[\w]+/g, "").trim();

        // Check if the note only contains tags
        if (!cleanNoteText && allTags.length > 0) {
          throw new Error("Note must contain text content, not just tags");
        }

        if (!activeWorkspaceId) {
          toast.error("No workspace selected. Please select a workspace.");
          throw new Error("No active workspace selected");
        }

        if (!isUserLoaded || !isSignedIn || !user || !user.id) {
          toast.error("Unable to identify user. Please sign in again.");
          throw new Error("No active user session");
        }

        const userId = user.id;

        toast.loading("Updating note...");
        const response = await dealsApi.editDealNote(
          editNote.noteId,
          cleanNoteText,
          activeWorkspaceId,
          deal.id,
          userId,
          allTags
        );

        // Find the new note ID from the response
        const updatedNoteId = response.application.deal_notes.find(
          (note) => note.note === cleanNoteText
        )?._id;

        // Update the deal with edited note
        const updatedDealNotes = deal.dealNotes.map((note) =>
          note._id === editNote.noteId
            ? {
                ...note,
                _id: updatedNoteId || note._id,
                note: cleanNoteText,
                created_on: new Date().toISOString(),
                tags: allTags,
              }
            : note
        );

        updateDeal({
          ...deal,
          dealNotes: updatedDealNotes,
        });

        return response;
      },
      onSuccess: () => {
        toast.dismiss();
        toast.success("Note updated successfully");
        posthog.capture("deal_note_updated", {
          workspace_id: activeWorkspaceId,
          user_id: user?.id,
          clerk_id: user?.id,
          deal_id: deal.id,
          company_id: deal.id,
          note_length: editNote.noteText.trim().replace(/#[\w]+/g, "").trim().length,
        });
        setEditNote({ isOpen: false, noteId: "", noteText: "", tags: [] });
      },
      onError: (error) => {
        toast.dismiss();
        const message =
          error instanceof Error
            ? error.message
            : "Failed to update note. Please try again.";
        toast.error(
          getErrorMessage(error, `Failed to update note: ${message}`)
        );
      },
    });

  // Add state for delete dialog
  const [deleteNote, setDeleteNote] = useState<{
    isOpen: boolean;
    noteId: string;
  }>({
    isOpen: false,
    noteId: "",
  });

  // Modify the mutation to accept noteId parameter
  const { mutate: deleteDealNoteMutation, isPending: isDeletePending } =
    useMutation({
      mutationFn: async (noteId: string) => {
        console.log("Delete mutation started with noteId:", noteId);
        if (!noteId) {
          console.error("Note ID is missing in mutation");
          throw new Error("Note ID is required");
        }

        if (!activeWorkspaceId) {
          toast.error("No workspace selected. Please select a workspace.");
          throw new Error("No active workspace selected");
        }

        toast.loading("Deleting note...");
        const response = await dealsApi.deleteDealNote(
          noteId,
          activeWorkspaceId,
          deal.id
        );

        // Update the deal by removing the deleted note
        const updatedDealNotes = deal.dealNotes.filter(
          (note) => note._id !== noteId
        );

        updateDeal({
          ...deal,
          dealNotes: updatedDealNotes,
        });

        return response;
      },
      onSuccess: () => {
        toast.dismiss();
        toast.success("Note deleted successfully");
        posthog.capture("deal_note_deleted", {
          workspace_id: activeWorkspaceId,
          user_id: user?.id,
          clerk_id: user?.id,
          deal_id: deal.id,
          company_id: deal.id,
        });
        setDeleteNote({ isOpen: false, noteId: "" });
      },
      onError: (error) => {
        toast.dismiss();
        const message =
          error instanceof Error
            ? error.message
            : "Failed to delete note. Please try again.";
        toast.error(
          getErrorMessage(error, `Failed to delete note: ${message}`)
        );
        console.error("Delete note error:", error);
      },
    });

  /** The composer's scope, named the way it reads in a sentence. */
  const scopeLabel = (scope: ComposeScope): string => {
    if (scope === "internal") return INTERNAL_SOURCE_LABEL.toLowerCase();
    if (scope === "board") return "the Raiseboard";
    const id = scope.slice("lookbook:".length);
    const lookbook = boardLookbooks.find((l) => l._id === id);
    return lookbook ? `lookbook "${lookbook.name}"` : "the lookbook";
  };

  const { mutate: addBoardCommentMutation, isPending: isBoardPending } =
    useMutation({
      mutationFn: async () => {
        const text = noteText.trim();
        if (!text) {
          toast.error("Comment cannot be empty");
          throw new Error("Comment cannot be empty");
        }
        if (!activeWorkspaceId) {
          toast.error("No workspace selected. Please select a workspace.");
          throw new Error("No active workspace selected");
        }

        toast.loading("Posting comment...");
        return dealBoardCommentsApi.add({
          opportunityId: activeWorkspaceId,
          companyId: deal.id,
          text,
          lookbookId: composeScope.startsWith("lookbook:")
            ? composeScope.slice("lookbook:".length)
            : undefined,
        });
      },
      onSuccess: (response) => {
        toast.dismiss();
        toast.success(`Comment posted to ${scopeLabel(composeScope)}`);
        posthog.capture("deal_board_comment_added", {
          workspace_id: activeWorkspaceId,
          user_id: user?.id,
          deal_id: deal.id,
          scope: composeScope,
          comment_length: response.comment.text.length,
        });
        setNoteText("");
        refreshBoardComments();
      },
      onError: (error) => {
        toast.dismiss();
        toast.error(getErrorMessage(error, "Failed to post comment"));
      },
    });

  const { mutate: deleteBoardCommentMutation } = useMutation({
    mutationFn: (commentId: string) => raiseBoardApi.deleteComment(commentId),
    onSuccess: () => {
      toast.success("Comment deleted");
      refreshBoardComments();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete comment"));
    },
  });

  const [deleteBoardComment, setDeleteBoardComment] = useState<{
    isOpen: boolean;
    commentId: string;
  }>({ isOpen: false, commentId: "" });

  // Helper function to safely create Date objects
  const createSafeDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (Number.isNaN(date.getTime())) {
        return null;
      }
      return date;
    } catch {
      return null;
    }
  };

  // New function to get all activities
  const getAuditLogEntries = () => {
    const entries: Array<{
      type: string;
      date: Date | null;
      description: string;
      tags?: string[];
    }> = [
      // Deal creation
      {
        type: "deal_created",
        date: createSafeDate(deal.overview.addedOn),
        description: "Deal was created",
      },
      // Files
      ...deal.company_files
        .map((file) => ({
          type: "file_uploaded",
          date: createSafeDate(file.time_stamp),
          description: `${file.file_type} was uploaded`,
        }))
        .filter((entry) => entry.date !== null),
      // AI Generations
      ...deal.aiGenerations.datasufficiency
        .map((ds) => ({
          type: "ai_analysis",
          date: createSafeDate(ds.created_on),
          description: "Data sufficiency analysis was generated",
        }))
        .filter((entry) => entry.date !== null),
      ...deal.aiGenerations.summaries
        .map((s) => ({
          type: "ai_analysis",
          date: createSafeDate(s.created_on),
          description: "Summary was generated",
        }))
        .filter((entry) => entry.date !== null),
      ...deal.aiGenerations.tearsheets
        .map((ts) => ({
          type: "ai_analysis",
          date: createSafeDate(ts.created_on),
          description: "Tearsheet was generated",
        }))
        .filter((entry) => entry.date !== null),
      ...deal.aiGenerations.startupscores
        .map((ss) => ({
          type: "ai_analysis",
          date: createSafeDate(ss.created_on),
          description: "Business risk was generated",
        }))
        .filter((entry) => entry.date !== null),
      ...deal.aiGenerations.metrics
        .map((m) => ({
          type: "ai_analysis",
          date: createSafeDate(m.created_on),
          description: "Metrics were generated",
        }))
        .filter((entry) => entry.date !== null),
      // Reviews
      ...deal.reviews
        .map((review) => ({
          type: "review_added",
          date: createSafeDate(review.created_on),
          description: `Review added, Score: ${review.overallRating}`,
        }))
        .filter((entry) => entry.date !== null),
      // Notes
      ...deal.dealNotes
        .map((note) => ({
          type: "note_added",
          date: createSafeDate(note.created_on),
          description: `Note added by ${note.created_by_user}`,
          content: note.note,
          tags: note.tags,
        }))
        .filter((entry) => entry.date !== null),
      // Raiseboard and lookbook comments
      ...boardComments
        .map((comment) => ({
          type: "board_comment_added",
          date: createSafeDate(comment.created_on),
          description: `${labelOfComment(comment)} comment by ${
            comment.user_name || "someone"
          }`,
          content: comment.text,
        }))
        .filter((entry) => entry.date !== null),
    ];

    const sortableEntries = entries.filter(
      (entry): entry is typeof entry & { date: Date } => entry.date !== null
    );

    return sortableEntries.sort((a, b) => b.date.getTime() - a.date.getTime());
  };

  // Get all unique tags from notes
  const allUsedTags = Array.from(
    new Set(
      (deal.dealNotes || [])
        .flatMap((note) => note.tags || [])
        .filter((tag) => tag?.trim())
    )
  );

  // Create options for MultiSelect
  const tagOptions = allUsedTags.map((tag) => ({
    label: tag,
    value: tag,
  }));

  // One feed: the workspace's own notes plus every board comment on this deal,
  // newest first, each tagged with where it came from. System-generated
  // `gap-fill-doc:` notes are dropped in buildWallEntries (SAAS-602).
  const wallEntries = useMemo(
    () => buildWallEntries(deal.dealNotes, boardComments),
    [deal.dealNotes, boardComments]
  );

  const sourceFilterOptions = useMemo(
    () =>
      sourceOptions(boardComments, boardLookbooks).map((o) => ({
        label: o.label,
        value: o.value,
      })),
    [boardComments, boardLookbooks]
  );

  const filteredEntries = filterWallEntries(
    wallEntries,
    filterSources as WallSource[],
    filterTags
  );

  const isBoardScope = composeScope !== "internal";
  const isSubmitting = isBoardScope ? isBoardPending : isPending;

  const auditEntries = getAuditLogEntries();

  return (
    <div>
      <Tabs defaultValue="notes" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="notes">Notes & Comments</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="notes">
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
                Notes &amp; Comments
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground">
                Your team's notes on this deal, alongside the comments left on
                it out on the Raiseboard and in your lookbooks. Filter by source
                to see one at a time.
              </p>

              {deal.company_files.length === 0 && (
                <div>
                  <Link
                    to={`/${activeWorkspaceId}/deals/${deal.id}?tab=data-room`}
                    className="inline-flex w-fit items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                  >
                    Add Dataroom Documents
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Add Note Form */}
          <div className="mb-6 sm:mb-8 rounded-lg border bg-card p-3 sm:p-4">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">Post to</span>
              <Select
                value={composeScope}
                onValueChange={(value) =>
                  setComposeScope(value as ComposeScope)
                }
              >
                <SelectTrigger className="h-8 w-auto min-w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">
                    {INTERNAL_SOURCE_LABEL}
                  </SelectItem>
                  {/* Only offered once the deal is actually published —
                      otherwise the comment lands on a thread nobody can
                      reach. Lookbooks imply publication, so they need no
                      equivalent check. */}
                  {deal.raiseBoard && (
                    <SelectItem value="board">Raiseboard</SelectItem>
                  )}
                  {boardLookbooks.map((lookbook) => (
                    <SelectItem
                      key={lookbook._id}
                      value={`lookbook:${lookbook._id}`}
                    >
                      Lookbook: {lookbook.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Posting anywhere but "Internal note" writes to a thread people
                outside the workspace read, which is not what this box has done
                until now — say so plainly rather than relying on the dropdown
                label alone. */}
            {isBoardScope && (
              <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 sm:p-3 text-xs sm:text-sm">
                <Globe className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                <span>
                  {composeScope === "board"
                    ? "This will be visible to everyone who can see this deal on the public Raiseboard and the global board."
                    : `This will be visible to anyone holding the link to ${scopeLabel(
                        composeScope
                      )}.`}
                </span>
              </div>
            )}

            <textarea
              className="min-h-[100px] w-full rounded-lg border bg-background p-2 sm:p-3 text-sm"
              placeholder={
                isBoardScope
                  ? `Add a comment to ${scopeLabel(composeScope)}`
                  : "Add a new note, use # to add tags"
              }
              value={noteText}
              onChange={(e) => {
                const newText = e.target.value;
                // Extract existing hashtags from the text
                const existingTags =
                  newText.match(/#(\w+)/g)?.map((tag) => tag.slice(1)) || [];
                // Update selected tags based on hashtags in text
                setSelectedTags(existingTags);
                // Set the full text including spaces
                setNoteText(newText);
              }}
            />
            {/* Tags are a deal-note feature; board comments have none. */}
            {!isBoardScope && (
              <div className="mt-3 flex flex-wrap gap-2">
                {availableTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant={
                      selectedTags.includes(tag) ? "default" : "secondary"
                    }
                    className="cursor-pointer hover:opacity-80"
                    onClick={() => {
                      setSelectedTags((prev) => {
                        const newTags = prev.includes(tag)
                          ? prev.filter((t) => t !== tag)
                          : [...prev, tag];

                        // Remove all existing hashtags
                        const textWithoutTags = noteText
                          .replace(/#\w+\s*/g, "")
                          .trim();
                        // Add all selected tags as hashtags at the end
                        const hashTags = newTags.map((t) => `#${t}`).join(" ");
                        setNoteText(
                          `${textWithoutTags}${hashTags ? " " + hashTags : ""}`
                        );

                        return newTags;
                      });
                    }}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            <div className="mt-3 sm:mt-4 flex justify-end">
              <button
                type="button"
                className="rounded-lg bg-primary px-3 sm:px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() =>
                  isBoardScope ? addBoardCommentMutation() : addDealNoteMutation()
                }
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? isBoardScope
                    ? "Posting..."
                    : "Adding..."
                  : isBoardScope
                    ? "Post Comment"
                    : "Add Note"}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="my-4 flex flex-wrap gap-2">
            <MultiSelect
              id={sourceFilterId}
              options={sourceFilterOptions}
              onValueChange={setFilterSources}
              placeholder="Filter by source..."
              value={filterSources}
              className="w-auto min-w-[220px] max-w-[400px]"
            />
            {tagOptions.length > 0 && (
              <MultiSelect
                id={tagFilterId}
                options={tagOptions}
                onValueChange={setFilterTags}
                placeholder="Filter by tags..."
                value={filterTags}
                className="w-auto min-w-[200px] max-w-[400px]"
              />
            )}
          </div>

          {/* Notes and board comments, newest first */}
          <div className="space-y-3 sm:space-y-4">
            {filteredEntries.length === 0 && (
              <p className="rounded-lg border border-dashed bg-card/50 p-6 text-center text-sm text-muted-foreground">
                {wallEntries.length === 0
                  ? "Nothing here yet. Add the first note."
                  : "Nothing matches the current filters."}
              </p>
            )}
            {filteredEntries.map((entry) => {
              if (entry.kind === "board") {
                return (
                  <div
                    key={entry.id}
                    className="rounded-lg border bg-card p-3 sm:p-4 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-col gap-1 min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="h-6 w-6 text-xs rounded-full bg-primary/10 flex items-center justify-center">
                            {entry.author.charAt(0)}
                          </div>
                          <span className="font-medium text-sm sm:text-base">
                            {entry.author}
                          </span>
                          <Badge variant="outline" className="gap-1">
                            <Globe className="h-3 w-3" />
                            {entry.sourceLabel}
                          </Badge>
                          <span className="text-xs sm:text-sm text-muted-foreground">
                            {entry.date.toLocaleString()}
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm text-card-foreground mt-2 break-words whitespace-pre-wrap">
                          {entry.text}
                        </p>
                      </div>
                      {/* No edit control: board comments have no edit
                          endpoint. Deletion is author-only, decided by the
                          server. */}
                      {entry.canDelete && (
                        <AlertDialog
                          open={
                            deleteBoardComment.isOpen &&
                            deleteBoardComment.commentId === entry.id
                          }
                          onOpenChange={(isOpen) => {
                            if (!isOpen) {
                              setDeleteBoardComment({
                                isOpen: false,
                                commentId: "",
                              });
                            }
                          }}
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 flex-shrink-0 text-destructive hover:text-destructive"
                            onClick={() =>
                              setDeleteBoardComment({
                                isOpen: true,
                                commentId: entry.id,
                              })
                            }
                            aria-label="Delete comment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Delete Comment
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                This removes it from {entry.sourceLabel} as well
                                as from here. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel
                                onClick={() =>
                                  setDeleteBoardComment({
                                    isOpen: false,
                                    commentId: "",
                                  })
                                }
                              >
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() =>
                                  deleteBoardCommentMutation(entry.id)
                                }
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                );
              }

              // Deal notes keep the card they always had; the shim below keeps
              // the field names the markup already uses.
              const note = {
                _id: entry.id,
                created_by_user: entry.author,
                created_by_user_id: entry.authorId,
                created_on: entry.date.toISOString(),
                note: entry.text,
                tags: entry.tags,
              };
              return (
              <div
                key={note._id}
                className="rounded-lg border bg-card p-3 sm:p-4 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-col gap-1 min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="h-6 w-6 text-xs rounded-full bg-primary/10 flex items-center justify-center">
                        {note.created_by_user.charAt(0)}
                      </div>
                      <span className="font-medium text-sm sm:text-base">
                        {note.created_by_user}
                      </span>
                      <span className="text-xs sm:text-sm text-muted-foreground">
                        {new Date(note.created_on).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-card-foreground mt-2 break-words whitespace-pre-wrap">
                      {note.note}
                    </p>
                    {note.tags && note.tags.length > 0 && (
                      <div className="flex items-start gap-2 mt-2 min-w-0">
                        <Tag className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="flex flex-wrap gap-2 min-w-0 flex-1">
                          {note.tags.map((tag) => (
                            <Badge
                              key={tag}
                              variant="secondary"
                              className="max-w-full whitespace-normal break-all"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {note.created_by_user_id === user?.id && (
                    <div className="flex gap-1">
                      <Dialog
                        open={editNote.isOpen && editNote.noteId === note._id}
                        onOpenChange={(isOpen) => {
                          if (isOpen) {
                            const existingTags = note.tags || [];
                            const hashTags = existingTags
                              .map((tag) => `#${tag}`)
                              .join(" ");
                            setEditNote({
                              isOpen: true,
                              noteId: note._id,
                              noteText: `${note.note}${
                                hashTags ? " " + hashTags : ""
                              }`,
                              tags: existingTags,
                            });
                          } else {
                            setEditNote({
                              isOpen: false,
                              noteId: "",
                              noteText: "",
                              tags: [],
                            });
                          }
                        }}
                      >
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 flex-shrink-0"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Edit Note</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <textarea
                              className="min-h-[100px] w-full rounded-lg border bg-background p-2 sm:p-3 text-sm"
                              value={editNote.noteText}
                              onChange={(e) => {
                                const newText = e.target.value;
                                // Extract existing hashtags from the text
                                const existingTags =
                                  newText
                                    .match(/#(\w+)/g)
                                    ?.map((tag) => tag.slice(1)) || [];
                                // Update selected tags based on hashtags in text
                                setEditNote((prev) => ({
                                  ...prev,
                                  tags: existingTags,
                                  noteText: newText,
                                }));
                              }}
                            />
                            {/* Add tags section */}
                            <div className="flex flex-wrap gap-2">
                              {availableTags.map((tag) => (
                                <Badge
                                  key={tag}
                                  variant={
                                    editNote.tags.includes(tag)
                                      ? "default"
                                      : "secondary"
                                  }
                                  className="cursor-pointer hover:opacity-80"
                                  onClick={() => {
                                    setEditNote((prev) => {
                                      const newTags = prev.tags.includes(tag)
                                        ? prev.tags.filter((t) => t !== tag)
                                        : [...prev.tags, tag];

                                      // Remove all existing hashtags
                                      const textWithoutTags = prev.noteText
                                        .replace(/#\w+\s*/g, "")
                                        .trim();
                                      // Add all selected tags as hashtags at the end
                                      const hashTags = newTags
                                        .map((t) => `#${t}`)
                                        .join(" ");

                                      return {
                                        ...prev,
                                        tags: newTags,
                                        noteText: `${textWithoutTags}${
                                          hashTags ? " " + hashTags : ""
                                        }`,
                                      };
                                    });
                                  }}
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                onClick={() =>
                                  setEditNote({
                                    isOpen: false,
                                    noteId: "",
                                    noteText: "",
                                    tags: [],
                                  })
                                }
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={() => editDealNoteMutation()}
                                disabled={isEditPending}
                              >
                                {isEditPending ? "Updating..." : "Update Note"}
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>

                      <AlertDialog
                        open={
                          deleteNote.isOpen && deleteNote.noteId === note._id
                        }
                        onOpenChange={(isOpen) => {
                          if (!isOpen) {
                            setDeleteNote({ isOpen: false, noteId: "" });
                          }
                        }}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 flex-shrink-0 text-destructive hover:text-destructive"
                          onClick={() => {
                            console.log(
                              "Setting note ID for deletion:",
                              note._id
                            );
                            setDeleteNote({ isOpen: true, noteId: note._id });
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Note</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this note? This
                              action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel
                              onClick={() =>
                                setDeleteNote({ isOpen: false, noteId: "" })
                              }
                            >
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => {
                                if (!deleteNote.noteId) {
                                  toast.error("Note ID is missing");
                                  return;
                                }
                                deleteDealNoteMutation(deleteNote.noteId);
                              }}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              disabled={isDeletePending || !deleteNote.noteId}
                            >
                              {isDeletePending ? "Deleting..." : "Delete"}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
                Audit Log
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground">
                Track all activities and changes in this deal
              </p>
            </div>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {auditEntries.slice(0, displayCount).map((entry) => (
              <div
                key={`${entry.type}-${entry.date.getTime()}-${
                  entry.description
                }`}
                className="rounded-lg border bg-card p-3 sm:p-4 shadow-sm"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs sm:text-sm font-medium">
                    {entry.date &&
                      formatDistanceToNow(entry.date, { addSuffix: true })}
                  </span>
                  <span className="text-xs sm:text-sm text-muted-foreground">
                    {entry.description}
                  </span>
                </div>
                {entry.date && (
                  <p className="text-xs sm:text-sm text-card-foreground mt-2">
                    {entry.date.toLocaleString()}
                  </p>
                )}
                {entry.tags && entry.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2 min-w-0">
                    {entry.tags.map((tag) => (
                      <Badge
                        key={tag}
                        variant="secondary"
                        className="max-w-full whitespace-normal break-all"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {displayCount < auditEntries.length && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="outline"
                  onClick={() => setDisplayCount((prev) => prev + 10)}
                >
                  Load More
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
